import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchCourses } from "../../slice/courseSlice";
import { Link } from "react-router-dom";

const categories = ["All", "Development", "Design", "Finance", "Marketing"];

const Courses = () => {
  const dispatch = useDispatch();
  const { courses = [], loading, error } = useSelector((state) => state.course);

  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(8);
  const [animatedCourseIds, setAnimatedCourseIds] = useState([]);
  const newCoursesRef = useRef(null);

  useEffect(() => {
    if (!courses || courses.length === 0) {
      dispatch(fetchCourses());
    }
  }, [dispatch, courses]);

  const filteredCourses =
    selectedCategory === "All"
      ? courses
      : courses.filter((course) => course.category === selectedCategory);

  const coursesToShow = filteredCourses.slice(0, visibleCount);
  const showViewButton = filteredCourses.length > 8;

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setVisibleCount(8);
    setAnimatedCourseIds([]);
    if (newCoursesRef.current) {
      newCoursesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const handleToggleView = () => {
    const previousCount = visibleCount;
    const nextCount =
      visibleCount >= filteredCourses.length ? 8 : visibleCount + 8;

    const newAnimatedIds = filteredCourses
      .slice(previousCount, nextCount)
      .map((course) => course._id || course.id);

    setVisibleCount(nextCount);
    setAnimatedCourseIds((prev) => [...prev, ...newAnimatedIds]);

    setTimeout(() => {
      if (newCoursesRef.current) {
        newCoursesRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
  };

  return (
    <div className="bg-white text-black px-4 pt-10 pb-24 font-sans">
      <h1 className="text-3xl sm:text-4xl font-bold text-[#fc3c04] text-center mb-10">
        Explore <span className="text-[#345bf3]">Courses</span>
      </h1>

      {/* Filters + Button */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div className="flex flex-wrap justify-center gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => handleCategoryChange(cat)}
              className={`px-4 py-1 rounded-lg border transition ${
                selectedCategory === cat
                  ? "bg-[#345bf3] text-white"
                  : "bg-white text-[#345bf3] border-[#345bf3]"
              } hover:bg-[#345bf3] hover:text-white`}
            >
              {cat}
            </button>
          ))}
        </div>

        {showViewButton && (
          <button
            onClick={handleToggleView}
            className="text-sm font-medium text-white border border-[#686104] bg-[#686104] px-8 py-2 rounded-full hover:bg-[#fc3c04] transition"
          >
            {visibleCount >= filteredCourses.length ? "View Less" : "View More"}
          </button>
        )}
      </div>

      {/* Courses Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
        {loading ? (
          <p className="text-center col-span-full text-gray-500">Loading...</p>
        ) : error ? (
          <p className="text-center col-span-full text-red-500">Error: {error}</p>
        ) : coursesToShow.length === 0 ? (
          <p className="text-center col-span-full text-gray-600">
            No courses available in this category.
          </p>
        ) : (
          coursesToShow.map((course, index) => {
            const courseId = course._id || course.id;
            if (!courseId) return null;

            const isAnimated = animatedCourseIds.includes(courseId);
            const fadeInClass = isAnimated ? "animate-fade-in-up" : "";

            return (
              <div
                key={courseId}
                ref={isAnimated && index === visibleCount - 8 ? newCoursesRef : null}
                className={`border border-gray-200 rounded-lg shadow-sm hover:shadow-lg transition transform bg-white flex flex-col group ${fadeInClass}`}
              >
                <Link to={`/course/${courseId}`}>
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL_REL}${course.image}`}
                    alt={course.title}
                    className="h-44 w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </Link>
                <div className="p-4 flex-grow flex flex-col justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-[#fc3c04] mb-1">
                      {course.title}
                    </h2>
                    <p className="text-sm mb-1">
                      <strong>Duration:</strong> {course.duration}
                    </p>
                    <p className="text-sm mb-1">
                      <strong>Fee:</strong> {course.fees}
                    </p>
                    <p className="text-sm mb-1">
                      <strong>Teacher:</strong> {course.teacherName}
                    </p>
                    <p className="text-sm mb-2">
                      <strong>Start On:</strong> {course.startOn}
                    </p>
                  </div>
                  <Link to={`/course/${courseId}`}>
                    <button className="mt-2 w-full bg-[#345bf3] text-white text-sm px-4 py-2 rounded hover:bg-[#fc3c04] transition">
                      Read More
                    </button>
                  </Link>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Courses;
